import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// TypeScript interfaces for our content
export interface AboutContent {
  id: string;
  title: string;
  description: string;
  features: string[];
  story_title: string;
  story_content: string;
  created_at: string;
  updated_at: string;
}

export interface PrivacySection {
  title: string;
  content: string;
}

export interface PrivacyContent {
  id: string;
  title: string;
  sections: PrivacySection[];
  created_at: string;
  updated_at: string;
}

export interface Post {
  id: string;
  title: string;
  description: string;
  content: string; // Markdown content
  content_type: 'markdown' | 'html';
  is_published: boolean;
  public_visibility: boolean; // Controls if published posts are visible in the grid
  order_index: number; // For ordering posts
  category: string; // New: category for the post
  route?: string; // Optional route for navigation
  created_at: string;
  updated_at: string;
}

export interface Feature {
  id: string;
  title: string;
  description: string;
  content: string;
  image_url: string;
  category?: string;
  route?: string;
  is_published: boolean;
  public_visibility: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface ContentResponse<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// Default fallback content
export const defaultAboutContent: AboutContent = {
  id: 'default',
  title: 'About LUT Rolls',
  description: 'LUT Rolls is a professional photo editing app that brings the power of cinematic color grading to your mobile device. With our carefully crafted filters, you can transform your photos with the same techniques used in films and professional photography.',
  features: [
    '40 Premium LUT Filters inspired by global cities',
    'Real-time preview with instant adjustments',
    'Professional-grade color grading',
    'High-resolution export up to 4K',
    'Intuitive and user-friendly interface'
  ],
  story_title: 'The Story',
  story_content: 'Each LUT in our collection is inspired by the unique atmosphere and mood of different cities around the world. From the misty overcast of Dublin to the golden warmth of Seville, every filter tells a story and evokes a specific emotion.',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

export const defaultPrivacyContent: PrivacyContent = {
  id: 'default',
  title: 'Privacy Policy',
  sections: [
    {
      title: 'Introduction',
      content: 'The LUT Rolls app respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our application.'
    },
    {
      title: 'Data Storage',
      content: 'Local storage: All LUTs data is stored locally on your device, no internet needed to access filters.'
    },
    {
      title: 'Third-Party Services',
      content: 'The LUT Rolls app does not integrate with third-party analytics services or advertising networks.'
    },
    {
      title: 'Changes to This Policy',
      content: 'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.'
    }
  ],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

// Default posts data (fallback)
export const defaultPosts: Post[] = [
  {
    id: 'default-1',
    title: 'Welcome to LUT Rolls',
    description: 'Discover the power of professional photo editing with our collection of cinematic filters.',
    content: `# Welcome to LUT Rolls

Transform your photos with **professional-grade color grading** that brings the cinematic experience to your mobile device.

## What makes LUT Rolls special?

- **40 Premium Filters** inspired by cities worldwide
- **Real-time Preview** with instant adjustments  
- **4K Export Quality** for professional results
- **No Internet Required** - all processing happens locally

Start your creative journey today!`,
    content_type: 'markdown',
    is_published: true,
    public_visibility: true,
    order_index: 1,
    category: 'general', // Default category
    route: '/general/welcome',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

// Default features data (fallback)
export const defaultFeatures: Feature[] = [
  {
    id: 'feature-1',
    title: 'Professional Color Grading',
    description: 'Transform your photos with cinematic LUT filters inspired by cities worldwide.',
    content: 'Experience professional-grade color grading with our collection of 40 premium LUT filters. Each filter is carefully crafted to capture the unique atmosphere of cities around the world, from the golden warmth of Mediterranean coastlines to the cool, misty tones of northern European cities.',
    image_url: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images/imageSelectorHome/santorini.JPG',
    category: 'editing',
    route: '/features/color-grading',
    is_published: true,
    public_visibility: true,
    order_index: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'feature-2',
    title: 'Real-time Preview',
    description: 'See your changes instantly with our real-time preview system.',
    content: 'Our advanced real-time preview system allows you to see the effects of your edits instantly. No more waiting for processing or rendering - every adjustment is reflected immediately in your viewfinder, making the editing process smooth and intuitive.',
    image_url: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images/imageSelectorHome/nairobi.JPG',
    category: 'interface',
    route: '/features/real-time-preview',
    is_published: true,
    public_visibility: true,
    order_index: 2,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'feature-3',
    title: '4K Export Quality',
    description: 'Export your edited photos in stunning 4K resolution for professional results.',
    content: 'Never compromise on quality. LUT Rolls supports high-resolution exports up to 4K, ensuring your edited photos maintain their crisp detail and professional quality whether you are sharing on social media or printing for display.',
    image_url: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images/imageSelectorHome/havana.JPG',
    category: 'export',
    route: '/features/4k-export',
    is_published: true,
    public_visibility: true,
    order_index: 3,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'feature-4',
    title: 'Cinematic Effects',
    description: 'Add authentic grain patterns, vintage textures, and atmospheric effects.',
    content: 'Enhance your photos with cinematic effects that add depth and character. From subtle film grain that adds warmth and authenticity to vintage textures that evoke nostalgia, these effects help create images with true cinematic appeal.',
    image_url: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images//lutrolls-examples.webp',
    category: 'effects',
    route: '/features/cinematic-effects',
    is_published: true,
    public_visibility: true,
    order_index: 4,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

// Image storage utilities
export const STORAGE_BUCKET = 'post-images'; // You'll need to create this bucket in Supabase

/**
 * Upload an image file to Supabase Storage
 * @param file - The image file to upload
 * @param path - Optional custom path (will generate one if not provided)
 * @returns Promise with the public URL of the uploaded image
 */
export async function uploadImage(file: File, path?: string): Promise<{ url: string; path: string } | null> {
  try {
    // Generate a unique path if not provided
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const filePath = path || `images/${timestamp}-${randomString}.${fileExtension}`;

    // Upload the file
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) {
      console.error('Upload error:', error);
      return null;
    }

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(filePath);

    return {
      url: publicUrl,
      path: filePath
    };
  } catch (error) {
    console.error('Upload failed:', error);
    return null;
  }
}

/**
 * Delete an image from Supabase Storage
 * @param path - The path of the image to delete
 * @returns Promise<boolean> - Success status
 */
export async function deleteImage(path: string): Promise<boolean> {
  try {
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([path]);

    if (error) {
      console.error('Delete error:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete failed:', error);
    return false;
  }
}

/**
 * Get a public URL for an image in storage
 * @param path - The path of the image
 * @returns string - The public URL
 */
export function getImageUrl(path: string): string {
  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(path);
  
  return publicUrl;
} 